import React, { createContext, useContext, useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const TagsContext = createContext(null);

export function TagsProvider({ children }) {
  const { user } = useAuth(); // Wait for authentication
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch tags from backend
  const fetchTags = async () => {
    try {
      const response = await api.get("/tags");
      setTags(response.data || []);
    } catch (e) {
      console.error("Failed to fetch tags:", e);
      // Optionally show an error, but usually better to fail silently for lists
    } finally {
      setLoading(false);
    }
  };

  // Only fetch when user is authenticated
  useEffect(() => {
    if (user) {
      fetchTags();
    } else {
      setLoading(true);
    }
  }, [user]);

  // Add a new tag
  const addTag = async (name) => {
    try {
      await api.post("/tags", { name });
      await fetchTags(); // Re-fetch to get the updated list
    } catch (e) {
      throw e; // Let the component handle the toast error
    }
  };

  // Delete a tag
  const deleteTag = async (name) => {
    try {
      // Encode the tag name in case it has special characters
      await api.delete(`/tags/${encodeURIComponent(name)}`);
      await fetchTags(); // Re-fetch to get the updated list
    } catch (e) {
      throw e; // Let the component handle the toast error
    }
  };

  return (
    <TagsContext.Provider value={{ tags, loading, fetchTags, refreshTags: fetchTags, addTag, deleteTag }}>
      {children}
    </TagsContext.Provider>
  );
}

export function useTags() {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error("useTags must be used within a TagsProvider");
  }
  return context;
}
